import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { http } from "@/lib/api";
import { auth, onFirebaseUser, signOutAll } from "@/lib/firebase";
import LoginModal from "@/components/LoginModal";
import WelcomeOverlay from "@/components/WelcomeOverlay";
import { toast } from "sonner";

const WELCOMED_KEY = "fp:welcomed";

const AuthCtx = createContext({
  user: null,
  loading: true,
  refresh: () => {},
  logout: () => {},
  exchangeToken: async () => {},
  openLogin: () => {},
  requireSignIn: () => false,
  celebrateLogin: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [welcome, setWelcome] = useState({ show: false, mode: "modal" });
  const exchangedFor = useRef(null); // firebase uid we last exchanged for

  // celebrateLogin() — call right after an explicit sign-in succeeds (never on
  // silent cookie/token rehydration). First-ever login gets the full-screen
  // welcome; returning farmers get the compact modal.
  const celebrateLogin = useCallback(() => {
    let firstTime = false;
    try {
      firstTime = !localStorage.getItem(WELCOMED_KEY);
      localStorage.setItem(WELCOMED_KEY, "1");
    } catch { /* ignore */ }
    setWelcome({ show: true, mode: firstTime ? "fullscreen" : "modal" });
  }, []);

  const closeWelcome = useCallback(() => setWelcome((w) => ({ ...w, show: false })), []);

  // QA seam — lets automated tests preview the post-login welcome without a
  // real Firebase/SSO round-trip. Fires the exact same overlay farmers see.
  useEffect(() => { window.__celebrateLogin = celebrateLogin; }, [celebrateLogin]);

  const openLogin = useCallback((reason) => {
    setShowLogin(true);
    if (reason) {
      // Also drop a soft breadcrumb in the console so testing/telemetry can see intent.
      // eslint-disable-next-line no-console
      console.info("[auth] openLogin →", reason);
    }
  }, []);

  // requireSignIn(topic) — call at the top of any handler that needs an auth
  // session. When there is no user we show an actionable toast that opens the
  // LoginModal and return false so the handler can early-exit.
  const requireSignIn = useCallback((topic) => {
    if (user) return true;
    const msg = topic ? `Sign in or create an account to ${topic}.` : "Sign in or create an account to continue.";
    toast.info(msg, {
      duration: 6000,
      action: { label: "Sign in / Sign up", onClick: () => setShowLogin(true) },
    });
    return false;
  }, [user]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const exchangeToken = useCallback(async (fbUser) => {
    if (!fbUser) return null;
    if (exchangedFor.current === fbUser.uid && user) return user;
    const idToken = await fbUser.getIdToken(/* forceRefresh */ false);
    const { data } = await http.post("/auth/firebase/session", { id_token: idToken });
    exchangedFor.current = fbUser.uid;
    setUser(data.user);
    setLoading(false);
    return data.user;
  }, [user]);

  useEffect(() => {
    // Initial hydrate from backend cookie (fast path — no Firebase call needed)
    refresh();
    // Listen for Firebase auth state; whenever the local Firebase user changes,
    // exchange the ID token with our backend to establish/refresh the SP session.
    const unsub = onFirebaseUser(async (fbUser) => {
      if (fbUser) {
        try {
          await exchangeToken(fbUser);
        } catch (e) {
          console.error("SP session exchange failed", e);
        }
      } else if (exchangedFor.current) {
        // Firebase signed out — clear backend cookie
        try { await http.post("/auth/logout"); } catch {}
        exchangedFor.current = null;
        setUser(null);
      }
    });
    return () => unsub && unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try { await signOutAll(); } catch {}
    try { await http.post("/auth/logout"); } catch {}
    exchangedFor.current = null;
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, refresh, logout, exchangeToken, openLogin, requireSignIn, celebrateLogin }}>
      {children}
      <LoginModal open={showLogin} onClose={() => setShowLogin(false)} />
      <WelcomeOverlay open={welcome.show} mode={welcome.mode} user={user} onClose={closeWelcome} />
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
